import React, { useState } from "react";
import "./CockpitExperiments.scss";

/**
 * Cockpit Experiments - Different approaches to a unified gaming command center
 * Each experiment explores a different way to organize and present all DevourPlay features
 */
const CockpitExperiments: React.FC = () => {
    const [activeExperiment, setActiveExperiment] = useState<"orbiter" | "grid" | "horizon" | "nexus">("orbiter");

    return (
        <div className={`cockpit-experiments cockpit-experiments--${activeExperiment}`}>
            {/* Experiment Selector */}
            <div className="experiment-selector">
                <button onClick={() => setActiveExperiment("orbiter")} className={activeExperiment === "orbiter" ? "active" : ""}>
                    ORBITER VIEW
                </button>
                <button onClick={() => setActiveExperiment("grid")} className={activeExperiment === "grid" ? "active" : ""}>
                    GRID COMMAND
                </button>
                <button onClick={() => setActiveExperiment("horizon")} className={activeExperiment === "horizon" ? "active" : ""}>
                    HORIZON LAYOUT
                </button>
                <button onClick={() => setActiveExperiment("nexus")} className={activeExperiment === "nexus" ? "active" : ""}>
                    NEXUS HUB
                </button>
            </div>

            {/* EXPERIMENT 1: ORBITER VIEW - Circular navigation with central hero */}
            {activeExperiment === "orbiter" &&
        <div className="orbiter-cockpit">
            <div className="orbiter-cockpit__background">
                <div className="orbit-ring orbit-ring--outer" />
                <div className="orbit-ring orbit-ring--middle" />
                <div className="orbit-ring orbit-ring--inner" />
            </div>

            <div className="orbiter-cockpit__center">
                <div className="hero-quest">
                    <div className="hero-quest__badge">🔥 FEATURED DROP</div>
                    <h1 className="hero-quest__title">MIDNIGHT MAYHEM</h1>
                    <p className="hero-quest__desc">Squad up and dominate three midnight matches</p>
                    <div className="hero-quest__rewards">
                        <div className="reward-badge">
                            <span className="reward-badge__amount">5,000</span>
                            <span className="reward-badge__type">XP</span>
                        </div>
                        <div className="reward-badge">
                            <span className="reward-badge__amount">500</span>
                            <span className="reward-badge__type">FUEL</span>
                        </div>
                    </div>
                    <button className="hero-quest__cta">START QUEST</button>
                </div>
            </div>

            <div className="orbiter-cockpit__modules">
                <div className="orbit-module" data-position="top">
                    <div className="module-icon">👤</div>
                    <div className="module-label">PROFILE</div>
                    <div className="module-value">LVL 42</div>
                </div>

                <div className="orbit-module" data-position="top-right">
                    <div className="module-icon">🎯</div>
                    <div className="module-label">QUESTS</div>
                    <div className="module-value">3 ACTIVE</div>
                </div>

                <div className="orbit-module" data-position="right">
                    <div className="module-icon">👥</div>
                    <div className="module-label">GUILD</div>
                    <div className="module-value">RANKED #7</div>
                </div>

                <div className="orbit-module" data-position="bottom-right">
                    <div className="module-icon">🎁</div>
                    <div className="module-label">LOOT</div>
                    <div className="module-value">2 READY</div>
                </div>

                <div className="orbit-module" data-position="bottom">
                    <div className="module-icon">⚡</div>
                    <div className="module-label">FUEL</div>
                    <div className="module-value">1,058</div>
                </div>

                <div className="orbit-module" data-position="bottom-left">
                    <div className="module-icon">🏆</div>
                    <div className="module-label">RANK</div>
                    <div className="module-value">TOP 5%</div>
                </div>

                <div className="orbit-module" data-position="left">
                    <div className="module-icon">🛍️</div>
                    <div className="module-label">SHOP</div>
                    <div className="module-value">NEW</div>
                </div>

                <div className="orbit-module" data-position="top-left">
                    <div className="module-icon">📊</div>
                    <div className="module-label">STATS</div>
                    <div className="module-value">↑12%</div>
                </div>
            </div>

            <div className="orbiter-cockpit__status-bar">
                <div className="status-item">
                    <span className="status-dot status-dot--online" />
                    <span>ONLINE</span>
                </div>
                <div className="status-item">
                    <span>SQUAD: 3/4</span>
                </div>
                <div className="status-item">
                    <span>NEXT REWARD: 2,847 XP</span>
                </div>
            </div>
        </div>
            }

            {/* EXPERIMENT 2: GRID COMMAND - Modular grid system like a command center */}
            {activeExperiment === "grid" &&
        <div className="grid-cockpit">
            <div className="grid-cockpit__header">
                <div className="header-stat">
                    <span className="header-stat__label">COMBAT XP</span>
                    <span className="header-stat__value">8,896</span>
                </div>
                <div className="header-stat">
                    <span className="header-stat__label">FUEL</span>
                    <span className="header-stat__value">1,058</span>
                </div>
                <div className="header-stat">
                    <span className="header-stat__label">GUILD</span>
                    <span className="header-stat__value">APEX LEGENDS</span>
                </div>
                <div className="header-stat header-stat--live">
                    <span className="live-indicator" />
                    <span>LIVE SESSION</span>
                </div>
            </div>

            <div className="grid-cockpit__layout">
                <div className="grid-module grid-module--profile">
                    <h3 className="module-title">PILOT STATUS</h3>
                    <div className="profile-content">
                        <div className="profile-avatar">
                            <div className="level-badge">42</div>
                        </div>
                        <div className="profile-stats">
                            <div className="stat-bar">
                                <span>TO NEXT LEVEL</span>
                                <div className="progress-bar">
                                    <div className="progress-fill" style={{width: "67%"}} />
                                </div>
                            </div>
                            <div className="mini-stats">
                                <span>WIN RATE: 64%</span>
                                <span>STREAK: 5</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid-module grid-module--missions">
                    <h3 className="module-title">ACTIVE MISSIONS</h3>
                    <div className="mission-list">
                        <div className="mission-item mission-item--urgent">
                            <div className="mission-timer">2H 14M</div>
                            <div className="mission-info">
                                <h4>SPEED DEMON</h4>
                                <p>Win 3 races in under 2 minutes</p>
                            </div>
                            <div className="mission-reward">750 XP</div>
                        </div>
                        <div className="mission-item">
                            <div className="mission-progress">
                                <div className="progress-ring">
                                    <span>2/5</span>
                                </div>
                            </div>
                            <div className="mission-info">
                                <h4>HEADSHOT MASTER</h4>
                                <p>Land 5 headshots in one match</p>
                            </div>
                            <div className="mission-reward">500 XP</div>
                        </div>
                    </div>
                </div>

                <div className="grid-module grid-module--activity">
                    <h3 className="module-title">SQUAD ACTIVITY</h3>
                    <div className="activity-feed">
                        <div className="activity-item">
                            <span className="activity-time">2m ago</span>
                            <span className="activity-text">DeadEye joined the squad</span>
                        </div>
                        <div className="activity-item">
                            <span className="activity-time">5m ago</span>
                            <span className="activity-text">Squad ranked up to #7</span>
                        </div>
                        <div className="activity-item">
                            <span className="activity-time">12m ago</span>
                            <span className="activity-text">New challenge available</span>
                        </div>
                    </div>
                </div>

                <div className="grid-module grid-module--quickactions">
                    <h3 className="module-title">QUICK ACTIONS</h3>
                    <div className="action-grid">
                        <button className="action-btn action-btn--primary">
                            <span className="action-icon">🎮</span>
                            <span>FIND MATCH</span>
                        </button>
                        <button className="action-btn">
                            <span className="action-icon">🎁</span>
                            <span>OPEN LOOT</span>
                        </button>
                        <button className="action-btn">
                            <span className="action-icon">👥</span>
                            <span>INVITE</span>
                        </button>
                        <button className="action-btn">
                            <span className="action-icon">🛍️</span>
                            <span>SHOP</span>
                        </button>
                    </div>
                </div>

                <div className="grid-module grid-module--leaderboard">
                    <h3 className="module-title">GUILD RANKINGS</h3>
                    <div className="leaderboard-list">
                        <div className="leaderboard-item leaderboard-item--self">
                            <span className="rank">#7</span>
                            <span className="name">APEX LEGENDS</span>
                            <span className="score">45,280 XP</span>
                        </div>
                        <div className="leaderboard-item">
                            <span className="rank">#6</span>
                            <span className="name">NIGHT RAIDERS</span>
                            <span className="score">46,100 XP</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
            }

            {/* EXPERIMENT 3: HORIZON LAYOUT - Horizontal timeline/journey style */}
            {activeExperiment === "horizon" &&
        <div className="horizon-cockpit">
            <div className="horizon-cockpit__skyline">
                <div className="skyline-element skyline-element--1" />
                <div className="skyline-element skyline-element--2" />
                <div className="skyline-element skyline-element--3" />
            </div>

            <div className="horizon-cockpit__timeline">
                <div className="timeline-track">
                    <div className="timeline-progress" style={{width: "35%"}} />
                </div>

                <div className="timeline-nodes">
                    <div className="timeline-node timeline-node--completed">
                        <div className="node-icon">✓</div>
                        <div className="node-label">DAILY LOGIN</div>
                        <div className="node-reward">+100 XP</div>
                    </div>

                    <div className="timeline-node timeline-node--completed">
                        <div className="node-icon">✓</div>
                        <div className="node-label">FIRST BLOOD</div>
                        <div className="node-reward">+250 XP</div>
                    </div>

                    <div className="timeline-node timeline-node--current">
                        <div className="node-icon">⚡</div>
                        <div className="node-label">WIN STREAK</div>
                        <div className="node-reward">+500 XP</div>
                        <div className="node-progress">2/3 WINS</div>
                    </div>

                    <div className="timeline-node timeline-node--upcoming">
                        <div className="node-icon">🎯</div>
                        <div className="node-label">ELITE QUEST</div>
                        <div className="node-reward">+1000 XP</div>
                    </div>

                    <div className="timeline-node timeline-node--locked">
                        <div className="node-icon">🔒</div>
                        <div className="node-label">MYSTERY</div>
                        <div className="node-reward">???</div>
                    </div>
                </div>
            </div>

            <div className="horizon-cockpit__panels">
                <div className="info-panel info-panel--stats">
                    <h3>YOUR STATS</h3>
                    <div className="stat-grid">
                        <div className="stat">
                            <span className="stat-value">42</span>
                            <span className="stat-label">LEVEL</span>
                        </div>
                        <div className="stat">
                            <span className="stat-value">8.9K</span>
                            <span className="stat-label">XP</span>
                        </div>
                        <div className="stat">
                            <span className="stat-value">1K</span>
                            <span className="stat-label">FUEL</span>
                        </div>
                    </div>
                </div>

                <div className="info-panel info-panel--featured">
                    <div className="featured-banner">
                        <span className="featured-tag">🔥 HOT DROP</span>
                        <h2>WEEKEND WARRIOR</h2>
                        <p>Double XP all weekend long</p>
                        <button className="featured-cta">JOIN NOW</button>
                    </div>
                </div>

                <div className="info-panel info-panel--social">
                    <h3>SQUAD STATUS</h3>
                    <div className="squad-list">
                        <div className="squad-member squad-member--online">
                            <span className="member-status" />
                            <span className="member-name">xXSniperXx</span>
                            <span className="member-activity">In Game</span>
                        </div>
                        <div className="squad-member squad-member--online">
                            <span className="member-status" />
                            <span className="member-name">ProGamer42</span>
                            <span className="member-activity">In Lobby</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
            }

            {/* EXPERIMENT 4: NEXUS HUB - Central hub with radiating sections */}
            {activeExperiment === "nexus" &&
        <div className="nexus-cockpit">
            <div className="nexus-cockpit__core">
                <div className="core-avatar">
                    <div className="avatar-ring avatar-ring--1" />
                    <div className="avatar-ring avatar-ring--2" />
                    <div className="avatar-center">
                        <span className="level">42</span>
                    </div>
                </div>
                <div className="core-stats">
                    <div className="core-stat">
                        <span className="stat-label">COMBAT RATING</span>
                        <span className="stat-value">ELITE</span>
                    </div>
                    <div className="xp-meter">
                        <div className="xp-fill" style={{width: "67%"}} />
                        <span className="xp-text">6,700 / 10,000 XP</span>
                    </div>
                </div>
            </div>

            <div className="nexus-cockpit__sectors">
                <div className="sector sector--quests">
                    <div className="sector-header">
                        <h3>QUEST LOG</h3>
                        <span className="sector-count">5</span>
                    </div>
                    <div className="sector-content">
                        <div className="quest-card quest-card--epic">
                            <span className="quest-rarity">EPIC</span>
                            <h4>LEGENDARY HUNTER</h4>
                            <div className="quest-progress">
                                <div className="progress-bar">
                                    <div className="progress-fill" style={{width: "40%"}} />
                                </div>
                                <span>2/5 Legendary Kills</span>
                            </div>
                            <div className="quest-rewards">
                                <span>2,000 XP</span>
                                <span>Epic Loot Box</span>
                            </div>
                        </div>
                        <div className="quest-card">
                            <h4>DAILY GRIND</h4>
                            <div className="quest-checklist">
                                <div className="checklist-item checklist-item--done">Play 3 Matches</div>
                                <div className="checklist-item">Win 1 Match</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sector sector--guild">
                    <div className="sector-header">
                        <h3>GUILD HQ</h3>
                        <span className="sector-badge">OFFICER</span>
                    </div>
                    <div className="sector-content">
                        <div className="guild-info">
                            <h4>APEX LEGENDS</h4>
                            <div className="guild-stats">
                                <span>MEMBERS: 47/50</span>
                                <span>RANK: #7</span>
                            </div>
                        </div>
                        <div className="guild-event">
                            <span className="event-timer">STARTS IN 2H</span>
                            <h5>GUILD WAR: TERRITORY TAKEOVER</h5>
                            <button className="event-join">PREPARE</button>
                        </div>
                    </div>
                </div>

                <div className="sector sector--rewards">
                    <div className="sector-header">
                        <h3>REWARD VAULT</h3>
                        <span className="sector-alert">2 NEW</span>
                    </div>
                    <div className="sector-content">
                        <div className="reward-grid">
                            <div className="reward-item reward-item--new">
                                <div className="reward-glow" />
                                <span className="reward-icon">🎁</span>
                                <span className="reward-name">Mystery Box</span>
                            </div>
                            <div className="reward-item reward-item--new">
                                <span className="reward-icon">⚡</span>
                                <span className="reward-name">500 Fuel</span>
                            </div>
                            <div className="reward-item">
                                <span className="reward-icon">🎯</span>
                                <span className="reward-name">XP Boost</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sector sector--store">
                    <div className="sector-header">
                        <h3>LOOT SHOP</h3>
                        <span className="sector-timer">REFRESHES IN 4H</span>
                    </div>
                    <div className="sector-content">
                        <div className="featured-item">
                            <div className="item-showcase">
                                <div className="item-rarity item-rarity--legendary" />
                                <span className="item-icon">🔥</span>
                            </div>
                            <div className="item-info">
                                <h5>INFERNO SKIN</h5>
                                <span className="item-price">2,500 FUEL</span>
                            </div>
                            <button className="item-buy">GET IT</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="nexus-cockpit__quickbar">
                <button className="quick-action">
                    <span>🎮</span>
                    <span>PLAY</span>
                </button>
                <button className="quick-action">
                    <span>👥</span>
                    <span>SQUAD</span>
                </button>
                <button className="quick-action">
                    <span>📊</span>
                    <span>STATS</span>
                </button>
                <button className="quick-action">
                    <span>⚙️</span>
                    <span>SETTINGS</span>
                </button>
            </div>
        </div>
            }
        </div>
    );
};

export default CockpitExperiments;